import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Loader2, ArrowLeft, Save, Eye } from "lucide-react";
import { toast } from "sonner";
import { GlassCard } from "../../components/common/ui";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Switch } from "../../components/ui/switch";
import RichTextEditor from "../../components/admin/RichTextEditor";
import MediaUploader from "../../components/admin/MediaUploader";
import SeoTab from "../../components/seo/SeoTab";
import api, { formatApiError } from "../../lib/api";

const selectCls = "w-full bg-black/30 border border-white/15 text-white rounded-md px-3 py-2 text-sm mt-1";
const inp = "mt-1 bg-black/30 border-white/15 text-white";

const EMPTY = {
  sku: "", name: "", slug: "", description: "", brand: "", category_id: "",
  cost_price: "", compare_at_price: "", retail_price: "",
  stock_status: "in_stock", is_active: true, is_featured: false,
  images: [], youtube_url: "",
  specs: { classification: "", firework_category: "", shots: "", duration_seconds: "", safety_distance_m: "", noise_level: "", effects_text: "" },
  seo: { auto: true },
};

function Section({ title, subtitle, children }) {
  return (
    <GlassCard className="p-6" hover={false}>
      <h2 className="font-playfair text-lg text-white">{title}</h2>
      {subtitle && <p className="text-xs text-white/40 mb-4 mt-1">{subtitle}</p>}
      <div className={`space-y-4 ${subtitle ? "" : "mt-4"}`}>{children}</div>
    </GlassCard>
  );
}
function Field({ label, children, className }) {
  return <div className={className}><Label className="text-white/80 text-sm">{label}</Label>{children}</div>;
}

export default function ProductForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [cats, setCats] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(!!id);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => { api.get("/admin/categories").then(({ data }) => setCats(data)).catch(() => {}); }, []);
  useEffect(() => { api.get("/admin/brands").then(({ data }) => setBrands(data)).catch(() => {}); }, []);
  useEffect(() => {
    if (id) {
      api.get(`/admin/products/${id}`).then(({ data }) => setForm({ ...EMPTY, ...data, specs: { ...EMPTY.specs, ...(data.specs || {}) } }))
        .catch(() => toast.error("Could not load product")).finally(() => setLoading(false));
    }
  }, [id]);

  useEffect(() => {
    const h = (e) => { if (dirty) { e.preventDefault(); e.returnValue = ""; } };
    window.addEventListener("beforeunload", h);
    return () => window.removeEventListener("beforeunload", h);
  }, [dirty]);

  const set = useCallback((k, v) => { setForm((f) => ({ ...f, [k]: v })); setDirty(true); }, []);
  const setSpec = useCallback((k, v) => { setForm((f) => ({ ...f, specs: { ...f.specs, [k]: v } })); setDirty(true); }, []);
  const num = (v) => (v === "" || v === null ? null : Number(v));

  const save = async () => {
    if (!form.sku.trim() || !form.name.trim()) { toast.error("SKU and product name are required"); return; }
    if (form.retail_price === "" || Number(form.retail_price) < 0) { toast.error("A valid Our Price is required"); return; }
    setSaving(true);
    const payload = {
      sku: form.sku.trim(), name: form.name.trim(), slug: form.slug || undefined,
      description: form.description, brand: form.brand, category_id: form.category_id || null,
      cost_price: num(form.cost_price), compare_at_price: num(form.compare_at_price), retail_price: num(form.retail_price),
      stock_status: form.stock_status, is_active: form.is_active, is_featured: form.is_featured,
      images: form.images, youtube_url: form.youtube_url || null,
      specs: {
        classification: form.specs.classification || null,
        firework_category: form.specs.firework_category || null,
        shots: num(form.specs.shots), duration_seconds: num(form.specs.duration_seconds),
        safety_distance_m: num(form.specs.safety_distance_m),
        noise_level: form.specs.noise_level || null, effects_text: form.specs.effects_text || null,
      },
      seo: form.seo || { auto: true },
    };
    try {
      if (id) await api.patch(`/admin/products/${id}`, payload);
      else await api.post("/admin/products", payload);
      setDirty(false);
      toast.success(id ? "Product updated" : "Product created");
      navigate("/admin/products");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Save failed");
    } finally { setSaving(false); }
  };

  const back = () => { if (dirty && !window.confirm("Discard unsaved changes?")) return; navigate("/admin/products"); };
  if (loading) return <div className="flex items-center gap-2 text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin text-gold" /> Loading…</div>;

  const margin = form.cost_price && form.retail_price ? (Number(form.retail_price) - Number(form.cost_price)) : null;

  return (
    <div className="space-y-6 pb-10" data-testid="product-form">
      <div className="flex items-center justify-between gap-4 sticky top-0 z-20 py-2 -mx-2 px-2 backdrop-blur">
        <button onClick={back} className="flex items-center gap-2 text-white/70 hover:text-white" data-testid="product-back-btn"><ArrowLeft className="h-4 w-4" /> Products</button>
        <div className="flex items-center gap-2">
          {id && form.slug && <a href={`/product/${form.slug}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-sm text-white/70 hover:text-gold" data-testid="product-preview-link"><Eye className="h-4 w-4" /> Preview</a>}
          <Button data-testid="save-product-btn" onClick={save} disabled={saving} className="cf-gold-btn font-semibold gap-2">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save Product</Button>
        </div>
      </div>
      <h1 className="font-playfair text-2xl sm:text-3xl text-white">{id ? "Edit Product" : "New Product"}</h1>

      <Section title="Product information">
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="SKU *"><Input data-testid="product-sku-input" value={form.sku} onChange={(e) => set("sku", e.target.value)} className={inp} /></Field>
          <Field label="Brand">
            <select data-testid="product-brand-select" value={form.brand || ""} onChange={(e) => set("brand", e.target.value)} className={selectCls}>
              <option value="">— Select a brand —</option>
              {brands.map((b) => <option key={b.brand_id} value={b.name}>{b.name}</option>)}
              {form.brand && !brands.some((b) => b.name === form.brand) && (
                <option value={form.brand}>{form.brand} (unlisted)</option>
              )}
            </select>
            {brands.length === 0 && <p className="text-xs text-white/40 mt-1">No brands yet — add them in Admin → Brands.</p>}
          </Field>
        </div>
        <Field label="Product name *"><Input data-testid="product-name-input" value={form.name} onChange={(e) => set("name", e.target.value)} className={inp} /></Field>
        <Field label="URL slug (auto if blank)"><Input data-testid="product-slug-input" value={form.slug} onChange={(e) => set("slug", e.target.value)} className={inp} /></Field>
        <Field label="Category">
          <select data-testid="product-category-select" className={selectCls} value={form.category_id || ""} onChange={(e) => set("category_id", e.target.value)}>
            <option value="">— None —</option>
            {cats.map((c) => <option key={c.category_id} value={c.category_id}>{c.name}</option>)}
          </select>
        </Field>
        <Field label="Description"><div className="mt-1"><RichTextEditor value={form.description} onChange={(v) => set("description", v)} /></div></Field>
      </Section>

      <Section title="Pricing" subtitle="Cost is admin-only and never shown publicly. Our Price is what customers pay; RRP shows as the struck-through 'was' price.">
        <div className="grid sm:grid-cols-3 gap-4">
          <Field label="Cost (inc VAT) — admin only"><Input data-testid="product-cost-input" type="number" step="0.01" min="0" value={form.cost_price ?? ""} onChange={(e) => set("cost_price", e.target.value)} className={inp} /></Field>
          <Field label="RRP"><Input data-testid="product-rrp-input" type="number" step="0.01" min="0" value={form.compare_at_price ?? ""} onChange={(e) => set("compare_at_price", e.target.value)} className={inp} /></Field>
          <Field label="Our Price *"><Input data-testid="product-price-input" type="number" step="0.01" min="0" value={form.retail_price} onChange={(e) => set("retail_price", e.target.value)} className={inp} /></Field>
        </div>
        {margin != null && <p className="text-xs text-white/40">Margin: £{margin.toFixed(2)}{form.retail_price ? ` (${((margin / Number(form.retail_price)) * 100).toFixed(0)}%)` : ""}</p>}
      </Section>

      <Section title="Stock">
        <Field label="Stock status" className="max-w-xs">
          <select data-testid="product-stock-select" className={selectCls} value={form.stock_status} onChange={(e) => set("stock_status", e.target.value)}>
            <option value="in_stock">In Stock</option>
            <option value="out_of_stock">Sold Out</option>
          </select>
        </Field>
        <div className="flex flex-wrap gap-6 pt-2">
          <label className="flex items-center gap-2 text-white/80 text-sm"><Switch data-testid="product-active-switch" checked={form.is_active} onCheckedChange={(v) => set("is_active", v)} /> Active (visible publicly)</label>
          <label className="flex items-center gap-2 text-white/80 text-sm"><Switch data-testid="product-featured-switch" checked={form.is_featured} onCheckedChange={(v) => set("is_featured", v)} /> Featured</label>
        </div>
      </Section>

      <Section title="Media" subtitle="Upload product images and add an optional YouTube demo video.">
        <MediaUploader value={form.images} onChange={(m) => set("images", m)} />
        <Field label="Demo video (YouTube URL)"><Input data-testid="product-youtube-input" value={form.youtube_url || ""} onChange={(e) => set("youtube_url", e.target.value)} className={inp} placeholder="https://youtu.be/…" /></Field>
      </Section>

      <Section title="Firework information">
        <div className="grid sm:grid-cols-3 gap-4">
          <Field label="Consumer category">
            <select data-testid="product-consumer-cat" className={selectCls} value={form.specs.classification || ""} onChange={(e) => setSpec("classification", e.target.value)}>
              <option value="">—</option><option value="F2">F2</option><option value="F3">F3</option>
            </select>
          </Field>
          <Field label="Firework category"><Input data-testid="product-firework-cat" value={form.specs.firework_category || ""} onChange={(e) => setSpec("firework_category", e.target.value)} className={inp} placeholder="Barrage, Rocket…" /></Field>
          <Field label="Number of shots"><Input type="number" value={form.specs.shots ?? ""} onChange={(e) => setSpec("shots", e.target.value)} className={inp} /></Field>
          <Field label="Duration (seconds)"><Input type="number" value={form.specs.duration_seconds ?? ""} onChange={(e) => setSpec("duration_seconds", e.target.value)} className={inp} /></Field>
          <Field label="Safety distance (m)"><Input type="number" value={form.specs.safety_distance_m ?? ""} onChange={(e) => setSpec("safety_distance_m", e.target.value)} className={inp} /></Field>
          <Field label="Noise level">
            <select className={selectCls} value={form.specs.noise_level || ""} onChange={(e) => setSpec("noise_level", e.target.value)}>
              <option value="">—</option><option value="low">Low</option><option value="medium">Medium</option><option value="loud">Loud</option>
            </select>
          </Field>
        </div>
        <Field label="Effects (free text)"><Textarea data-testid="product-effects" value={form.specs.effects_text || ""} onChange={(e) => setSpec("effects_text", e.target.value)} className={inp} placeholder="e.g. Gold willow, crackling, red peonies…" /></Field>
      </Section>

      <Section title="Search Engine Optimisation (SEO)" subtitle="Automatic by default. Turn off 'Use Automatic SEO' to customise, or use 'Generate with AI'.">
        <SeoTab
          entityType="product"
          entity={{ product_id: id, name: form.name, brand: form.brand, retail_price: Number(form.retail_price) || 0, short_description: form.short_description, description: form.description, images: form.images, stock_status: form.stock_status, sku: form.sku, specs: form.specs, category_id: form.category_id, secondary_category_ids: form.secondary_category_ids, tags: form.tags }}
          value={form.seo}
          onChange={(seo) => set("seo", seo)}
          slug={form.slug}
          onSlugChange={(v) => set("slug", v)}
        />
      </Section>
    </div>
  );
}
